/**
 * Pure model for Witness Dash.
 *
 * A boundary crossing move is generated only when the agent is in the gate
 * lane, inside the action zone, and holding every required witness.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import levelsData from "../data/witnessDashLevels.json" with { type: "json" };

export interface WitnessDashPickupSpec {
  readonly id: string;
  readonly label: string;
  readonly witnessId: string;
  readonly lane: number;
  readonly spawnAtMs: number;
  readonly speed: number;
}

export interface WitnessDashGateSpec {
  readonly id: string;
  readonly label: string;
  readonly regime: string;
  readonly lane: number;
  readonly spawnAtMs: number;
  readonly speed: number;
  readonly requiredWitnesses: readonly string[];
  readonly impact: number;
}

export interface WitnessDashLevel {
  readonly id: string;
  readonly name: string;
  readonly brief: string;
  readonly initialIntegrity: number;
  readonly targetCrossings: number;
  readonly lanes: readonly string[];
  readonly pickups: readonly WitnessDashPickupSpec[];
  readonly gates: readonly WitnessDashGateSpec[];
}

export interface WitnessDashPickup {
  readonly id: string;
  readonly label: string;
  readonly witnessId: string;
  readonly lane: number;
  readonly x: number;
  readonly speed: number;
}

export interface WitnessDashGate {
  readonly id: string;
  readonly label: string;
  readonly regime: string;
  readonly lane: number;
  readonly x: number;
  readonly speed: number;
  readonly requiredWitnesses: readonly string[];
  readonly impact: number;
}

export type WitnessDashStatus = "playing" | "cleared" | "escaped";

export interface WitnessDashState {
  readonly levelId: string;
  readonly levelName: string;
  readonly brief: string;
  readonly timeMs: number;
  readonly lane: number;
  readonly pickupCursor: number;
  readonly gateCursor: number;
  readonly inventory: readonly string[];
  readonly pickups: readonly WitnessDashPickup[];
  readonly gates: readonly WitnessDashGate[];
  readonly integrity: number;
  readonly crossings: number;
  readonly missed: number;
  readonly status: WitnessDashStatus;
  readonly lastEvent: string;
}

export interface WitnessDashLaneMove {
  readonly type: "lane";
  readonly lane: number;
}

export interface WitnessDashCrossMove {
  readonly type: "cross";
  readonly gateId: string;
  readonly requiredWitnesses: readonly string[];
}

export type WitnessDashMove = WitnessDashLaneMove | WitnessDashCrossMove;

interface WitnessDashLevelsData {
  readonly levels: readonly WitnessDashLevel[];
}

const AGENT_X = 18;
const COLLECT_RADIUS = 5;
const CROSS_MIN_X = 10;
const CROSS_MAX_X = 25;

export const WITNESS_DASH_STEP_MS = 1000 / 30;
export const WITNESS_DASH_LEVELS = (levelsData as WitnessDashLevelsData).levels;
export const DEFAULT_WITNESS_DASH_LEVEL_ID = WITNESS_DASH_LEVELS[0]?.id ?? "";

const LEVEL_BY_ID = new Map(
  WITNESS_DASH_LEVELS.map((level) => [level.id, level]),
);

export function getWitnessDashLevel(levelId: string): WitnessDashLevel {
  const level = LEVEL_BY_ID.get(levelId);
  if (!level) throw new Error(`Unknown Witness Dash level: ${levelId}`);
  return level;
}

export function createWitnessDashState(
  levelId = DEFAULT_WITNESS_DASH_LEVEL_ID,
): WitnessDashState {
  const level = getWitnessDashLevel(levelId);
  return {
    levelId: level.id,
    levelName: level.name,
    brief: level.brief,
    timeMs: 0,
    lane: 0,
    pickupCursor: 0,
    gateCursor: 0,
    inventory: [],
    pickups: [],
    gates: [],
    integrity: level.initialIntegrity,
    crossings: 0,
    missed: 0,
    status: "playing",
    lastEvent:
      "Dash through boundary gates only when the required witness tuple is held.",
  };
}

export function tickWitnessDash(
  state: WitnessDashState,
  deltaMs = WITNESS_DASH_STEP_MS,
): WitnessDashState {
  if (state.status !== "playing") return state;

  const level = getWitnessDashLevel(state.levelId);
  const nextTimeMs = state.timeMs + Math.max(0, deltaMs);
  let pickupCursor = state.pickupCursor;
  let gateCursor = state.gateCursor;
  const spawnedPickups: WitnessDashPickup[] = [];
  const spawnedGates: WitnessDashGate[] = [];

  while (
    pickupCursor < level.pickups.length &&
    level.pickups[pickupCursor].spawnAtMs <= nextTimeMs
  ) {
    const pickup = level.pickups[pickupCursor];
    spawnedPickups.push({
      id: `${pickup.id}:${pickupCursor}`,
      label: pickup.label,
      witnessId: pickup.witnessId,
      lane: pickup.lane,
      x: 100,
      speed: pickup.speed,
    });
    pickupCursor += 1;
  }

  while (
    gateCursor < level.gates.length &&
    level.gates[gateCursor].spawnAtMs <= nextTimeMs
  ) {
    const gate = level.gates[gateCursor];
    spawnedGates.push({
      id: `${gate.id}:${gateCursor}`,
      label: gate.label,
      regime: gate.regime,
      lane: gate.lane,
      x: 100,
      speed: gate.speed,
      requiredWitnesses: gate.requiredWitnesses,
      impact: gate.impact,
    });
    gateCursor += 1;
  }

  const seconds = Math.max(0, deltaMs) / 1000;
  const inventory = [...state.inventory];
  const pickups: WitnessDashPickup[] = [];
  for (const pickup of [...state.pickups, ...spawnedPickups].map((item) => ({
    ...item,
    x: item.x - item.speed * seconds,
  }))) {
    const collectible =
      pickup.lane === state.lane &&
      Math.abs(pickup.x - AGENT_X) <= COLLECT_RADIUS;
    if (collectible) {
      inventory.push(pickup.witnessId);
    } else if (pickup.x >= -4) {
      pickups.push(pickup);
    }
  }

  const gates: WitnessDashGate[] = [];
  let integrity = state.integrity;
  let missed = state.missed;
  for (const gate of [...state.gates, ...spawnedGates].map((item) => ({
    ...item,
    x: item.x - item.speed * seconds,
  }))) {
    if (gate.x < 4) {
      integrity -= gate.impact;
      missed += 1;
    } else {
      gates.push(gate);
    }
  }

  if (integrity <= 0) {
    return {
      ...state,
      timeMs: nextTimeMs,
      pickupCursor,
      gateCursor,
      inventory,
      pickups,
      gates,
      integrity: 0,
      missed,
      status: "escaped",
      lastEvent:
        "A boundary route escaped the witness tuple discipline too many times.",
    };
  }

  if (
    pickupCursor >= level.pickups.length &&
    gateCursor >= level.gates.length &&
    pickups.length === 0 &&
    gates.length === 0 &&
    state.crossings < level.targetCrossings
  ) {
    return {
      ...state,
      timeMs: nextTimeMs,
      pickupCursor,
      gateCursor,
      inventory,
      pickups,
      gates,
      integrity,
      missed,
      status: "escaped",
      lastEvent:
        "The run ended before enough boundary crossings carried complete witness tuples.",
    };
  }

  return {
    ...state,
    timeMs: nextTimeMs,
    pickupCursor,
    gateCursor,
    inventory,
    pickups,
    gates,
    integrity,
    missed,
  };
}

export function legalWitnessDashMoves(
  state: WitnessDashState,
): readonly WitnessDashMove[] {
  if (state.status !== "playing") return [];
  const level = getWitnessDashLevel(state.levelId);
  return [
    ...level.lanes
      .map((_, lane) => lane)
      .filter((lane) => lane !== state.lane)
      .map((lane) => ({ type: "lane" as const, lane })),
    ...legalWitnessDashCrossMoves(state),
  ];
}

export function legalWitnessDashCrossMoves(
  state: WitnessDashState,
): readonly WitnessDashCrossMove[] {
  if (state.status !== "playing") return [];
  return state.gates
    .filter((gate) => gate.lane === state.lane)
    .filter((gate) => gate.x >= CROSS_MIN_X && gate.x <= CROSS_MAX_X)
    .filter((gate) => hasAll(state.inventory, gate.requiredWitnesses))
    .map((gate) => ({
      type: "cross" as const,
      gateId: gate.id,
      requiredWitnesses: gate.requiredWitnesses,
    }));
}

export function unwitnessedCrossingGenerated(): boolean {
  return false;
}

export function stepWitnessDash(
  state: WitnessDashState,
  move: WitnessDashMove,
): WitnessDashState {
  if (state.status !== "playing") return state;

  if (move.type === "lane") {
    const level = getWitnessDashLevel(state.levelId);
    if (move.lane < 0 || move.lane >= level.lanes.length) return state;
    return {
      ...state,
      lane: move.lane,
      lastEvent: `Moved to ${level.lanes[move.lane]}.`,
    };
  }

  const legal = legalWitnessDashCrossMoves(state).find(
    (candidate) => candidate.gateId === move.gateId,
  );
  if (!legal) return state;

  const level = getWitnessDashLevel(state.levelId);
  const gate = state.gates.find((item) => item.id === move.gateId);
  if (!gate) return state;

  const crossings = state.crossings + 1;
  const next: WitnessDashState = {
    ...state,
    crossings,
    gates: state.gates.filter((item) => item.id !== move.gateId),
    inventory: consumeMany(state.inventory, legal.requiredWitnesses),
    lastEvent: `${gate.label} crossed with ${legal.requiredWitnesses.join(", ")}.`,
  };

  if (crossings >= level.targetCrossings) {
    return {
      ...next,
      status: "cleared",
      lastEvent:
        "Boundary run complete: every counted crossing carried its witness tuple.",
    };
  }

  return next;
}

export function witnessGateInActionZone(gate: WitnessDashGate): boolean {
  return gate.x >= CROSS_MIN_X && gate.x <= CROSS_MAX_X;
}

function hasAll(
  inventory: readonly string[],
  required: readonly string[],
): boolean {
  const remaining = [...inventory];
  for (const witness of required) {
    const index = remaining.indexOf(witness);
    if (index < 0) return false;
    remaining.splice(index, 1);
  }
  return true;
}

function consumeMany(
  inventory: readonly string[],
  required: readonly string[],
): readonly string[] {
  const remaining = [...inventory];
  for (const witness of required) {
    const index = remaining.indexOf(witness);
    if (index >= 0) remaining.splice(index, 1);
  }
  return remaining;
}
